import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { setIsGlobalLoading } from '@/store/slices/siteConfig';
import { AppEnvironmentT, GuidT } from '@/types/common';
import { getAllRoutePaths, path } from '@/routes/Routes.helpers';
import { privateRoutes } from '@/routes/Routes.private.config';
import { publicRoutes } from '@/routes/Routes.public.config';
import { authApi } from '@/api/authApi/authApi';
import { refreshToken as refreshAuthToken } from '@/helpers/authRefresh';
import { detectAppEnvironment } from '@/helpers/detectAppEnvironment';
import { logout } from '@/helpers/logout';
import { getCookie } from '@/helpers/storage';
import { getFormattedPathName } from '@/helpers/utils';

export const useAuthNavigation = (): void => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const dispatch = useDispatch();

    const [
        getCurrentUser,
        { data: currentUser, isLoading: isCurrentUserLoading },
    ] = authApi.useLazyGetCurrentUserQuery();

    const [isTokenRefreshing, setIsTokenRefreshing] = useState<boolean>(false);
    const [environment, setEnvironment] = useState<AppEnvironmentT>('unknown');

    useEffect(() => {
        setEnvironment(detectAppEnvironment());
    }, []);

    useEffect(() => {
        const token = getCookie('token');
        const refreshToken = getCookie('refreshToken');
        const userGuid: GuidT | null = getCookie('userGuid');

        const formattedPath = getFormattedPathName(pathname);

        if (environment === 'unknown') {
            return;
        }

        switch (true) {
            case environment === 'telegram' && formattedPath === path('auth'): {
                navigate(path('authTg'), { replace: true });
                return;
            }

            case environment === 'web' && formattedPath === path('authTg'): {
                navigate(path('auth'), { replace: true });
                return;
            }

            case getAllRoutePaths(privateRoutes).includes(formattedPath): {
                if (token && !currentUser && !isCurrentUserLoading) {
                    getCurrentUser();
                    return;
                }

                if (!token && refreshToken && userGuid && !isTokenRefreshing) {
                    dispatch(setIsGlobalLoading(true));
                    setIsTokenRefreshing(true);

                    refreshAuthToken(refreshToken, userGuid)
                        .then((data) => {
                            if (data) {
                                getCurrentUser();
                            } else {
                                void logout(true);
                            }
                        })
                        .finally(() => {
                            dispatch(setIsGlobalLoading(false));
                            setIsTokenRefreshing(false);
                        });

                    return;
                }

                if (token && (!refreshToken || !userGuid)) {
                    void logout(true);
                    return;
                }

                if (!token) {
                    navigate(
                        environment === 'telegram'
                            ? path('authTg')
                            : path('auth'),
                        {
                            replace: true,
                        },
                    );
                    return;
                }

                return;
            }
            case getAllRoutePaths(publicRoutes).includes(formattedPath): {
                if (token) {
                    navigate(path('home'), { replace: true });
                }
                return;
            }
            default: {
                return;
            }
        }
    }, [
        pathname,
        currentUser,
        isCurrentUserLoading,
        isTokenRefreshing,
        environment,
    ]);
};
